import { adminApi } from '@/features/admin';
import { getStorageUrl } from '@/shared/lib';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, CheckCircle, Eye, XCircle } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

export default function AdminPhotosPage() {
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin-photos', page],
    queryFn: () => adminApi.getPhotosToValidate(page, 20),
  });

  const verifyMutation = useMutation({
    mutationFn: ({ nodeId, verified }: { nodeId: number; verified: boolean }) =>
      adminApi.verifyPhoto(nodeId, verified),
    onSuccess: (_, variables) => {
      toast.success(variables.verified ? 'Photo validee' : 'Photo rejetee');
      queryClient.invalidateQueries({ queryKey: ['admin-photos'] });
    },
    onError: () => toast.error('Erreur lors de la mise a jour'),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Validation des photos</h1>
        <Badge variant="outline">
          <Camera className="h-3 w-3 mr-1" />
          {data?.total || 0} photo(s) en attente
        </Badge>
      </div>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {[...Array(8)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="aspect-square w-full rounded-lg mb-4" />
                <Skeleton className="h-4 w-3/4 mb-2" />
                <Skeleton className="h-8 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : data?.photos.length ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {data.photos.map((photo) => (
            <Card key={photo.nodeId} className="overflow-hidden">
              <CardContent className="p-4">
                <div className="relative aspect-square mb-4 bg-muted rounded-lg overflow-hidden">
                  <img
                    src={getStorageUrl(photo.photo)}
                    alt={photo.username}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Link
                      to={`/admin/users/${photo.nodeId}`}
                      className="font-medium hover:underline"
                    >
                      {photo.username}
                    </Link>
                    <Button variant="ghost" size="icon" asChild>
                      <Link to={`/admin/users/${photo.nodeId}`}>
                        <Eye className="h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      className="flex-1 bg-green-500 hover:bg-green-600"
                      size="sm"
                      onClick={() =>
                        verifyMutation.mutate({ nodeId: photo.nodeId, verified: true })
                      }
                      disabled={verifyMutation.isPending}
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Valider
                    </Button>
                    <Button
                      variant="destructive"
                      className="flex-1"
                      size="sm"
                      onClick={() =>
                        verifyMutation.mutate({ nodeId: photo.nodeId, verified: false })
                      }
                      disabled={verifyMutation.isPending}
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Rejeter
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-8 text-center">
            <Camera className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold mb-2">Aucune photo en attente</h2>
            <p className="text-muted-foreground">Toutes les photos ont ete validees.</p>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {data && data.total > 20 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Precedent
          </Button>
          <span className="flex items-center px-4">
            Page {page} / {Math.ceil(data.total / 20)}
          </span>
          <Button
            variant="outline"
            onClick={() => setPage((p) => p + 1)}
            disabled={page >= Math.ceil(data.total / 20)}
          >
            Suivant
          </Button>
        </div>
      )}
    </div>
  );
}
